import { ScanDataInMemorySchema, ScanRootSchema, ScanTemplateRootSchema } from '@kaiizen/kai-scan-template-system';
import {
  boolean,
  buffer,
  datetimeString,
  number,
  optional,
  optionalQueryBoolean,
  optionalString,
  string,
  z,
  zodLess,
  type ZodLessInfer,
} from '@kaiizen/zod-less';
import { endPoint } from '@kaiizen/zod-less-api';
import {
  AllProductSupplierOrgsSchema,
  ApiAbortScanVideoMultipartUploadResponseSchema,
  ApiAbortScanVideoMultipartUploadSchema,
  ApiActivePropertyProjectSchema,
  ApiActivityLogListResponseSchema,
  ApiActivityLogSortOrderSchema,
  ApiAllOrgSettingsSchema,
  ApiApiKeySchema,
  ApiAudienceDocShareLinkCreateResponseSchema,
  ApiAudienceDocShareLinkRevokeResponseSchema,
  ApiAudienceDocShareLinkStatusSchema,
  ApiBackgroundJobSchema,
  ApiBatchUploadOrgPropertiesResponseSchema,
  ApiCompleteIssueVideoUploadResponseSchema,
  ApiCompleteIssueVideoUploadSchema,
  ApiCompleteScanVideoAudioUploadResponseSchema,
  ApiCompleteScanVideoAudioUploadSchema,
  ApiCompleteScanVideoMultipartUploadSchema,
  ApiCompleteScanVideoUploadResponseSchema,
  ApiCompleteScanVideoUploadSchema,
  ApiCreateApiKeyResponseSchema,
  ApiCreateApiKeySchema,
  ApiCreateOperationSchema,
  ApiCreateOrgContractorSchema,
  ApiCreateOrgMarketSchema,
  ApiCreateOrgProductSchema,
  ApiCreateOrgSchema,
  ApiCreatePropertyProjectSchema,
  ApiCreateScanDataSuggestionSchema,
  ApiCreateScanNoteSchema,
  ApiCreateScanPhotoSchema,
  ApiCreateScanSchema,
  ApiCreateScanTemplateSchema,
  ApiFeatureFlagRecordSchema,
  ApiFeatureFlagsSchema,
  ApiFindPropertyProjectsSchema,
  ApiFoundPropertyProjectSchema,
  ApiGetScanVideoAudiosByIdsPayloadSchema,
  ApiGetScanVideosByIdsPayloadSchema,
  ApiHandoffPropertyProjectSchema,
  ApiHeartbeatSchema,
  ApiInitIssueVideoUploadResponseSchema,
  ApiInitIssueVideoUploadSchema,
  ApiInitScanVideoAudioUploadResponseSchema,
  ApiInitScanVideoAudioUploadSchema,
  ApiInitScanVideoMultipartUploadResponseSchema,
  ApiInitScanVideoMultipartUploadSchema,
  ApiInitScanVideoUploadResponseSchema,
  ApiInitScanVideoUploadSchema,
  ApiIssueVideoSubmissionListQuerySchema,
  ApiIssueVideoSubmissionListResponseSchema,
  ApiIssueVideoSubmissionSchema,
  ApiNotificationListResponseSchema,
  ApiNotificationReadResponseSchema,
  ApiOperationSchema,
  ApiOrgContractorSchema,
  ApiOrgMarketSchema,
  ApiOrgProductSchema,
  ApiOrgPropertySchema,
  ApiOrgSchema,
  ApiOrgSettingsSchema,
  ApiOrgSupplierSchema,
  ApiOrgWithRoleSchema,
  ApiPropertyProjectSchema,
  ApiReportTypeSchema,
  ApiScanDataSuggestionSchema,
  ApiScanNoteSchema,
  ApiScanPhotoSchema,
  ApiScanPhotoStatusSchema,
  ApiScanSchema,
  ApiScanTemplateSchema,
  ApiScanVideoAudioSchema,
  ApiScanVideoMultipartStatusResponseSchema,
  ApiScanVideoSchema,
  ApiScanVideoSubmissionSchema,
  ApiSetFeatureFlagSchema,
  ApiSetSupplierAccessSchema,
  ApiSignScanVideoUploadPartsResponseSchema,
  ApiSignScanVideoUploadPartsSchema,
  ApiSupplierAccessOverviewSchema,
  ApiSupplierAccessRecordSchema,
  ApiTakePropertyProjectWorkSchema,
  ApiUpdateApiKeySchema,
  ApiUpdateOrgContractorSchema,
  ApiUpdateOrgMarketSchema,
  ApiUpdateOrgProductSchema,
  ApiUpdateOrgPropertySchema,
  ApiUpdateOrgSchema,
  ApiUpdateOrgSettingsSchema,
  ApiUpdatePropertyProjectSchema,
  ApiUpdateScanDataSuggestionsByScanVideoSchema,
  ApiUpdateScanDataSuggestionSchema,
  ApiUpdateScanSchema,
  ApiUpdateScanTemplateSchema,
  ApiUpdateUserSchema,
  ApiUserOrgRoleSchema,
  ApiUserOrgSchema,
  ApiUserPermissionsSchema,
  ApiUserSchema,
  ApiUserToScanSchema,
  ApiUserToScanWithUserSchema,
  ApiUserWithRoleSchema,
  ApiVivifyOrgPropertySchema,
  ArchivedListSearchQuerySchema,
  AuthCheckSessionRequestPayloadSchema,
  AuthCheckSessionResponseSchema,
  AuthDeleteAccountResponseSchema,
  AuthGetExpoTokensResponseSchema,
  AuthGetQrCodeQuerySchema,
  AuthGetQrCodeResponseSchema,
  AuthInitiateEmailRequestPayloadSchema,
  AuthInitiateRequestPayloadSchema,
  AuthInitiateResponseSchema,
  AuthInitiateSmsRequestPayloadSchema,
  AuthInitiateSmsResponseSchema,
  AuthSyncExpoTokensRequestPayloadSchema,
  AuthSyncExpoTokensResponseSchema,
  AuthValidateEmailRequestPayloadSchema,
  AuthValidateResponseSchema,
  AuthValidateSmsRequestPayloadSchema,
  CombinedProductSearchRequestQuerySchema,
  CombinedProductSearchResponseSchema,
  JevEvaluatePayloadSchema,
  JevEvaluateResponseSchema,
  LimitSchema,
  ListSearchQuerySchema,
  OperationListQuerySchema,
  ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS,
  ORG_PROPERTIES_CSV_REQUIRED_COLUMNS,
  PropertyProjectQuerySchema,
  PropertyProjectSortBySchema,
  PropertySearchQuerySchema,
  RetailerProductSchema,
  RetailerProductSearchRequestQuerySchema,
  StreetViewImageRequestQuerySchema,
} from './ApiSchemas';

const numberOrNumberFromString = z.union([z.string(), z.number()]).transform(v => typeof v === 'string' ? parseInt(v) : v);

export const MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE = 1000;

const scanPartTreeHistoryQuery = {
  scanPartId: string.meta({ description: 'The scanPartId at the root of the tree. History includes this part and all its descendants.' }),
  from: optional(datetimeString).meta({ description: 'from is inclusive - all returned records have updatedAt >= `from`.' }),
  to: optional(datetimeString).meta({ description: 'to is exclusive - all returned records have updatedAt < `to`.' }),
  toScanDataId: optional(string).meta({
    description:
      "Requires `to`. Pagination cursor: also returns records with updatedAt = `to` whose id sorts after `toScanDataId`. To fetch the next page, pass the last returned record's updatedAt as `to` and its id as `toScanDataId`.",
  }),
  limit: optional(numberOrNumberFromString).meta({
    description: `Maximum records to return. Defaults to, and is capped at, ${MAX_SCAN_PART_TREE_HISTORY_PAGE_SIZE}.`,
  }),
};

/** A ScanData version as history reports it: the saved record plus who saved it. */
export const ScanPartHistoryRecordSchema = ScanDataInMemorySchema.and(zodLess({ userId: optionalString }));
export type ScanPartHistoryRecord = ZodLessInfer<typeof ScanPartHistoryRecordSchema>;

const scanPartTreeHistoryOutput = zodLess([ScanPartHistoryRecordSchema]).meta({
  description:
    'Every version of the scan part and its descendants, ordered by updatedAt DESC then id DESC. If the result length equals the limit, there may be more records.',
});

export const KaiProApiEndpoints = {
  heartbeat: endPoint({
    method: 'GET',
    path: '/api/heartbeat',
    input: {},
    output: ApiHeartbeatSchema,
    openapi: { description: 'Checks that the API is running and responding. Super admins also get server health details.' },
  }),

  // ***********************************************************************************
  // Auth
  // ***********************************************************************************
  validateSession: endPoint({ // applies to both send and receive SMS auth flows
    method: 'POST',
    path: '/api/auth/validate',
    input: {},
    output: AuthValidateResponseSchema,
    openapi: { description: 'checks if the current session is valid.' },
  }),
  initiateReceiveSmsAuth: endPoint({
    method: 'POST',
    path: '/api/auth/initiate',
    input: { payload: AuthInitiateRequestPayloadSchema },
    output: AuthInitiateResponseSchema,
    openapi: { description: 'Initiate authentication where the user will send US a verification code via SMS or WhatsApp to validate their session.' },
  }),
  initiateSendSmsAuth: endPoint({
    method: 'POST',
    path: '/api/auth/initiate-sms',
    input: { payload: AuthInitiateSmsRequestPayloadSchema },
    output: AuthInitiateSmsResponseSchema,
    openapi: { description: 'Initiate authentication where Kai sends a verification code to the user via SMS or email.' },
  }),
  initiateSendEmailAuth: endPoint({
    method: 'POST',
    path: '/api/auth/initiate-email',
    input: { payload: AuthInitiateEmailRequestPayloadSchema },
    output: AuthInitiateSmsResponseSchema,
    openapi: { description: 'Initiate authentication where Kai sends a verification code to the user via email.' },
  }),
  validateSendSmsAuth: endPoint({
    method: 'POST',
    path: '/api/auth/validate-sms',
    input: { payload: AuthValidateSmsRequestPayloadSchema },
    output: AuthCheckSessionResponseSchema,
    openapi: { description: 'Validate the 6-digit code sent to the user via SMS.' },
  }),
  validateSendEmailAuth: endPoint({
    method: 'POST',
    path: '/api/auth/validate-email',
    input: { payload: AuthValidateEmailRequestPayloadSchema },
    output: AuthCheckSessionResponseSchema,
    openapi: { description: 'Validate the 6-digit code sent to the user via email.' },
  }),
  syncExpoTokens: endPoint({
    method: 'POST',
    path: '/api/auth/sync-expo-tokens',
    input: { payload: AuthSyncExpoTokensRequestPayloadSchema },
    output: AuthSyncExpoTokensResponseSchema,
    openapi: { description: 'Sync Expo push notification tokens for the authenticated user.' },
  }),
  getExpoTokens: endPoint({
    method: 'GET',
    path: '/api/auth/expo-tokens',
    input: {},
    output: AuthGetExpoTokensResponseSchema,
    openapi: { description: "Get the authenticated user's Expo push notification tokens." },
  }),
  deleteAccount: endPoint({
    method: 'DELETE',
    path: '/api/auth/delete-account',
    input: {},
    output: AuthDeleteAccountResponseSchema,
    openapi: { description: "Delete the authenticated user's account. This is irreversible and will remove all user data." },
  }),
  checkReceiveSmsAuth: endPoint({
    method: 'POST',
    path: '/api/auth/check-session',
    input: { key: string, payload: AuthCheckSessionRequestPayloadSchema },
    output: AuthCheckSessionResponseSchema,
    openapi: { description: 'Check if a session has been authorized and get the token if so.', keyName: 'sessionId' },
  }),
  switchOrg: endPoint({
    method: 'POST',
    path: '/api/auth/switch-org',
    input: { key: string, query: { userId: optional(string) } },
    output: { token: string },
    openapi: { description: 'Switch the org for a session.', keyName: 'orgId' },
  }),
  getQrCode: endPoint({ // used in receive-sms auth flow
    method: 'GET',
    path: '/api/auth/qr-code',
    input: { key: string, query: AuthGetQrCodeQuerySchema },
    output: AuthGetQrCodeResponseSchema,
    openapi: { description: 'Get QR code for authentication.', keyName: 'sessionId' },
  }),

  // ***********************************************************************************
  // User
  // ***********************************************************************************
  getUser: endPoint({
    method: 'GET',
    path: '/api/user-v2',
    input: { key: string },
    output: ApiUserSchema,
    openapi: { description: 'Get user information.' },
  }),
  updateUser: endPoint({
    method: 'PATCH',
    path: '/api/user',
    input: { key: string, payload: ApiUpdateUserSchema },
    output: ApiUserSchema,
    openapi: { description: 'Update user information.', keyName: 'userId' },
  }),
  getUsers: endPoint({
    method: 'GET',
    path: '/api/users',
    input: { query: ListSearchQuerySchema },
    output: [ApiUserWithRoleSchema],
    openapi: { description: 'Get users by organization ID.' },
  }),
  getUserPermissions: endPoint({
    method: 'GET',
    path: '/api/user-permissions',
    input: { key: string },
    output: ApiUserPermissionsSchema,
    openapi: { description: 'Get user permissions for a specific organization.' },
  }),
  getUserSearch: endPoint({
    method: 'GET',
    path: '/api/user-search',
    input: { query: { query: string } },
    output: [ApiUserSchema],
    openapi: { description: 'Search users.' },
  }),

  // ***********************************************************************************
  // API keys
  // ***********************************************************************************
  getApiKeys: endPoint({
    method: 'GET',
    path: '/api/api-keys',
    input: {},
    output: [ApiApiKeySchema],
    openapi: { description: 'List non-secret API-key metadata for the current organization.' },
  }),
  createApiKey: endPoint({
    method: 'POST',
    path: '/api/api-key',
    input: { payload: ApiCreateApiKeySchema },
    output: ApiCreateApiKeyResponseSchema,
    openapi: { description: 'Create an API key and return its raw value exactly once.' },
  }),
  updateApiKey: endPoint({
    method: 'PATCH',
    path: '/api/api-key',
    input: { key: string, payload: ApiUpdateApiKeySchema },
    output: ApiApiKeySchema,
    openapi: { description: 'Rename an API-key machine user or update its role.', keyName: 'apiKeyId' },
  }),
  revokeApiKey: endPoint({
    method: 'DELETE',
    path: '/api/api-key',
    input: { key: string },
    output: ApiApiKeySchema,
    openapi: { description: 'Revoke an API key. Revoked keys remain visible for audit history.', keyName: 'apiKeyId' },
  }),

  // ***********************************************************************************
  // Feature Flags
  // ***********************************************************************************
  getCurrentFeatureFlags: endPoint({
    method: 'GET',
    path: '/api/feature-flags',
    input: {},
    output: ApiFeatureFlagsSchema,
    openapi: { description: 'Get effective feature flags for the current authenticated user (priority: user > org > global).' },
  }),
  setFeatureFlag: endPoint({
    method: 'POST',
    path: '/api/feature-flag',
    input: { payload: ApiSetFeatureFlagSchema },
    output: ApiSetFeatureFlagSchema,
    openapi: { description: 'Create or update a feature flag at global, org, or user scope. Super admin only.' },
  }),
  getAllFeatureFlagRecords: endPoint({
    method: 'GET',
    path: '/api/feature-flag-all',
    input: {},
    output: [ApiFeatureFlagRecordSchema],
    openapi: { description: 'Get all feature flag records. Super admin only.' },
  }),
  deleteFeatureFlag: endPoint({
    method: 'DELETE',
    path: '/api/feature-flag',
    input: { key: string },
    output: ApiFeatureFlagRecordSchema,
    openapi: { description: 'Delete a feature flag record by ID. Super admin only.', keyName: 'featureFlagId' },
  }),
  getOrgSuppliers: endPoint({
    method: 'GET',
    path: '/api/org-suppliers',
    input: {},
    output: [ApiOrgSupplierSchema],
    openapi: { description: 'Get the suppliers the current organization can use.' },
  }),
  getSupplierAccessOverview: endPoint({
    method: 'GET',
    path: '/api/supplier-access-all',
    input: {},
    output: ApiSupplierAccessOverviewSchema,
    openapi: { description: 'Get every warehouse supplier and every supplier access row. Super admin only.' },
  }),
  setSupplierAccess: endPoint({
    method: 'POST',
    path: '/api/supplier-access',
    input: { payload: ApiSetSupplierAccessSchema },
    output: ApiSupplierAccessRecordSchema,
    openapi: { description: "Set a supplier's default access or an org override. Super admin only." },
  }),
  deleteSupplierAccess: endPoint({
    method: 'DELETE',
    path: '/api/supplier-access',
    input: { key: string },
    output: ApiSupplierAccessRecordSchema,
    openapi: { description: 'Delete a supplier access row by ID. Super admin only.', keyName: 'supplierAccessId' },
  }),

  // ***********************************************************************************
  // Notifications
  // ***********************************************************************************
  getNotifications: endPoint({
    method: 'GET',
    path: '/api/notifications',
    input: { query: { limit: optional(numberOrNumberFromString), cursor: optionalString } },
    output: ApiNotificationListResponseSchema,
    openapi: { description: 'Get notifications for the current user.' },
  }),
  markNotificationsRead: endPoint({
    method: 'POST',
    path: '/api/notifications/read',
    input: { payload: { ids: optional([string]), markAll: optional(z.boolean()) } },
    output: ApiNotificationReadResponseSchema,
    openapi: { description: 'Mark notifications read for the current user.' },
  }),

  getProjectActivityLog: endPoint({
    method: 'GET',
    path: '/api/activity-log/project',
    input: {
      key: string,
      query: {
        limit: optional(numberOrNumberFromString),
        cursor: optionalString,
        order: ApiActivityLogSortOrderSchema.optional(),
        eventTypes: optionalString.meta({ description: 'Comma-separated event types; when given, only entries of those types are returned.' }),
      },
    },
    output: ApiActivityLogListResponseSchema,
    openapi: { description: 'Get project-scoped activity log entries.', keyName: 'projectId' },
  }),

  getPropertyActivityLog: endPoint({
    method: 'GET',
    path: '/api/activity-log/property',
    input: {
      key: string,
      query: {
        limit: optional(numberOrNumberFromString),
        cursor: optionalString,
        order: ApiActivityLogSortOrderSchema.optional(),
        eventTypes: optionalString.meta({ description: 'Comma-separated event types; when given, only entries of those types are returned.' }),
      },
    },
    output: ApiActivityLogListResponseSchema,
    openapi: { description: 'Get activity log entries for every project at a property, archived ones included.', keyName: 'orgPropertyId' },
  }),

  // ***********************************************************************************
  // org
  // ***********************************************************************************
  getOrg: endPoint({
    method: 'GET',
    path: '/api/org',
    input: { key: string },
    output: ApiOrgSchema,
    openapi: { description: 'Get an org by ID. If the org does not exist, this will return 404.' },
  }),
  getCurrentOrg: endPoint({
    method: 'GET',
    path: '/api/org-current',
    input: {},
    output: ApiOrgWithRoleSchema,
    openapi: { description: 'Get current organization.' },
  }),
  getOrgIdForResource: endPoint({
    method: 'GET',
    path: '/api/org-id-for-resource',
    input: {
      query: {
        scanId: optional(string),
        propertyProjectId: optional(string),
        orgPropertyId: optional(string),
        orgMarketId: optional(string),
        scanTemplateId: optional(string),
        orgContractorId: optional(string),
      },
    },
    output: z.object({ orgId: string }),
    openapi: { description: 'Get the org required to access a resource. Use when access denied to find which org to switch to.' },
  }),
  createOrg: endPoint({
    method: 'POST',
    path: '/api/org',
    input: { payload: ApiCreateOrgSchema },
    output: ApiOrgSchema,
    openapi: { description: 'Create a new org. Super admin only.' },
  }),
  updateOrg: endPoint({
    method: 'PATCH',
    path: '/api/org',
    input: { key: string, payload: ApiUpdateOrgSchema },
    output: ApiOrgSchema,
    openapi: { description: 'Update an existing org. Only super admins can change isLive.' },
  }),
  deleteOrg: endPoint({
    method: 'DELETE',
    path: '/api/org',
    input: { key: string },
    output: ApiOrgSchema,
    openapi: { description: 'Delete an org.' },
  }),
  seedOrg: endPoint({
    method: 'POST',
    path: '/api/org-seed',
    input: { key: string },
    output: z.object({
      seeded: z.boolean(),
      message: z.string(),
      records: z.object({
        market: optional(ApiOrgMarketSchema),
        contractor: optional(ApiOrgContractorSchema),
        template: optional(ApiScanTemplateSchema),
      }),
    }),
    openapi: { description: 'Seed an org with example data' },
  }),
  getOrgsByUserId: endPoint({
    method: 'GET',
    path: '/api/org-by-user-id',
    input: { key: string },
    output: [ApiOrgWithRoleSchema],
    openapi: { description: 'Get all orgs for a user.', keyName: 'userId' },
  }),
  getAllOrgs: endPoint({
    method: 'GET',
    path: '/api/org-all',
    input: {},
    output: [ApiOrgSchema],
    openapi: { description: 'Get all orgs.' },
  }),
  getAllProductSupplierOrgs: endPoint({
    method: 'GET',
    path: '/api/product-supplier',
    input: {},
    output: AllProductSupplierOrgsSchema,
    openapi: { description: 'Get all product supplier organizations without permission restrictions.' },
  }),
  addUserToOrg: endPoint({
    method: 'POST',
    path: '/api/org-add-user',
    input: { key: string, query: { userId: string, role: ApiUserOrgRoleSchema } },
    output: ApiUserOrgSchema,
    openapi: { description: 'Add a user to an org by user ID. Requires admin or manager role in the organization.', keyName: 'orgId' },
  }),
  addUserToOrgByPhoneNumber: endPoint({
    method: 'POST',
    path: '/api/org-add-user-by-phone-number',
    input: {
      key: string,
      payload: { phoneNumber: optional(string), role: ApiUserOrgRoleSchema, name: optional(string), email: optional(string), userId: optional(string) },
    },
    output: ApiUserOrgSchema,
    openapi: {
      description: "Add a user to an org by phone number. Creates the user if they don't exist. Requires admin or manager role in the organization.",
      keyName: 'orgId',
    },
  }),
  removeUserFromOrg: endPoint({
    method: 'POST',
    path: '/api/org-remove-user',
    input: { key: string, query: { userId: string } },
    output: ApiUserOrgSchema,
    openapi: {
      description: 'Remove a user from an org. Requires admin or manager role in the organization. Users cannot remove themselves.',
      keyName: 'orgId',
    },
  }),
  updateUserOrgRole: endPoint({
    method: 'PATCH',
    path: '/api/org-patch-user-role',
    input: { key: string, query: { userId: string, role: ApiUserOrgRoleSchema } },
    output: ApiUserOrgSchema,
    openapi: {
      description: 'Update the role of a user in an org. Requires admin or manager role in the organization. Users cannot change their own role.',
      keyName: 'orgId',
    },
  }),

  // ***********************************************************************************
  // OrgProperty
  // ***********************************************************************************
  getOrgProperty: endPoint({
    method: 'GET',
    path: '/api/org-property',
    input: { key: string },
    output: ApiOrgPropertySchema,
    openapi: { description: 'Get organization property.' },
  }),
  getOrgProperties: endPoint({
    method: 'GET',
    path: '/api/org-properties',
    input: { query: { archived: optionalQueryBoolean } },
    output: [ApiOrgPropertySchema],
    openapi: { description: 'Get organization properties for the current organization.' },
  }),
  vivifyOrgProperty: endPoint({
    method: 'POST',
    path: '/api/org-property-vivify',
    input: { payload: ApiVivifyOrgPropertySchema },
    output: ApiOrgPropertySchema,
    openapi: { description: 'Vivify organization property.' },
  }),
  batchUploadOrgProperties: endPoint({
    method: 'POST',
    path: '/api/org-property-batch-upload',
    input: { file: z.any() },
    output: ApiBatchUploadOrgPropertiesResponseSchema,
    openapi: {
      description: `Super admin only: upload a CSV with header row ${ORG_PROPERTIES_CSV_REQUIRED_COLUMNS.join(', ')} (optional columns: ${
        ORG_PROPERTIES_CSV_OPTIONAL_COLUMNS.join(', ')
      }). Vivifies org properties for each row's org in concurrent batches of 10 rows. customId is stored as the property's customer property id (customerMetadata.propertyId). Market may be empty (no market) or a case-insensitive active market name in that org. A property that already exists is returned unchanged.`,
    },
  }),
  updateOrgProperty: endPoint({
    method: 'PATCH',
    path: '/api/org-property',
    input: { key: string, payload: ApiUpdateOrgPropertySchema },
    output: ApiOrgPropertySchema,
    openapi: { description: 'Update organization property.' },
  }),

  // ***********************************************************************************
  // Property search
  // ***********************************************************************************
  searchProperties: endPoint({
    method: 'GET',
    path: '/api/properties/search',
    input: { query: PropertySearchQuerySchema },
    output: [ApiOrgPropertySchema],
    openapi: { description: 'Typeahead search across active properties, with an option to include archived properties.' },
  }),

  // ***********************************************************************************
  // PropertyProject
  // ***********************************************************************************
  getPropertyProject: endPoint({
    method: 'GET',
    path: '/api/property-project',
    input: { key: string },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Get property project.' },
  }),
  getPropertyProjects: endPoint({
    method: 'GET',
    path: '/api/property-projects',
    input: { query: PropertyProjectQuerySchema },
    output: [ApiPropertyProjectSchema],
    openapi: { description: 'Get property projects by search query, market ID, archive filters, and optional limit.' },
  }),

  createPropertyProject: endPoint({
    method: 'POST',
    path: '/api/property-project',
    input: { payload: ApiCreatePropertyProjectSchema },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Create a new property project.' },
  }),
  updatePropertyProject: endPoint({
    method: 'PATCH',
    path: '/api/property-project',
    input: { key: string, payload: ApiUpdatePropertyProjectSchema },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Update property project.' },
  }),
  handoffPropertyProject: endPoint({
    method: 'POST',
    path: '/api/property-project-handoff',
    input: { key: string, payload: ApiHandoffPropertyProjectSchema },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Hand off responsibility for an editable project scope.' },
  }),
  takePropertyProjectWork: endPoint({
    method: 'POST',
    path: '/api/property-project-take-work',
    input: { key: string, payload: ApiTakePropertyProjectWorkSchema },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Take responsibility for an editable project scope as a project member.' },
  }),
  findPropertyProjects: endPoint({
    method: 'POST',
    path: '/api/property-projects-find',
    input: { payload: ApiFindPropertyProjectsSchema },
    output: [ApiFoundPropertyProjectSchema],
    openapi: {
      description:
        "Find the current org's properties and projects from a list of queries, one result per query in order. A query matches a project id or property id, then a customer property id (ignoring case, whitespace, dashes and underscores), then an address (exact, else the one property whose address starts with it). A property resolves to its most recent active project, else its most recent archived one.",
    },
  }),
  reactivatePropertyProject: endPoint({
    method: 'POST',
    path: '/api/property-project-reactivate',
    input: { key: string },
    output: ApiPropertyProjectSchema,
    openapi: { description: 'Reactivate a completed property project (status: completed -> active).' },
  }),

  /**
   * @deprecated Use getPropertyProjects instead.
   */
  getPropertyProjectsByOrgPropertyId: endPoint({
    method: 'GET',
    path: '/api/property-project-by-org-property-id',
    input: { key: string, query: { limit: optional(string), archived: optionalQueryBoolean } },
    output: [ApiPropertyProjectSchema],
    openapi: { description: 'Get property projects by organization property ID.', deprecated: true },
  }),
  /**
   * @deprecated Use getPropertyProjects instead.
   */
  getActivePropertyProjectsByMarketId: endPoint({
    method: 'GET',
    path: '/api/active-property-projects-by-market-id',
    input: { key: string },
    output: [ApiActivePropertyProjectSchema],
    openapi: { description: 'Get active property projects (projects with active scans) for a market.', keyName: 'orgMarketId', deprecated: true },
  }),
  /**
   * @deprecated Use getPropertyProjects instead.
   */
  getActivePropertyProjectsForUser: endPoint({
    method: 'GET',
    path: '/api/active-property-projects-for-user',
    input: { query: { limit: optional(numberOrNumberFromString) } },
    output: [ApiActivePropertyProjectSchema],
    openapi: { description: 'Get active property projects where the user is assigned to active scans.', keyName: 'userId', deprecated: true },
  }),
  /**
   * @deprecated Use getPropertyProjects instead.
   */
  getActivePropertyProjects: endPoint({
    method: 'GET',
    path: '/api/active-property-projects',
    input: {
      query: {
        limit: optional(LimitSchema),
        search: optionalString,
        orgMarketId: optionalString,
        assignedToCurrentUser: optionalQueryBoolean,
        sortBy: optional(PropertyProjectSortBySchema),
      },
    },
    output: [ApiActivePropertyProjectSchema],
    openapi: {
      description: 'Get active property projects with optional market filter, current-user assignment filter, search, limit, and sort mode.',
      deprecated: true,
    },
  }),

  // ***********************************************************************************
  // street view
  // ***********************************************************************************
  getStreetViewImage: endPoint({
    method: 'GET',
    path: '/api/street-view-image',
    input: { query: StreetViewImageRequestQuerySchema },
    output: buffer,
    openapi: { description: 'Get street view image.' },
  }),

  // ***********************************************************************************
  // Scan Photo
  // ***********************************************************************************
  initIssueVideoUpload: endPoint({
    method: 'POST',
    path: '/api/org-video-upload-init',
    input: { payload: ApiInitIssueVideoUploadSchema },
    output: ApiInitIssueVideoUploadResponseSchema,
    openapi: { description: 'Initiate an authenticated direct-to-S3 issue video upload.' },
  }),
  completeIssueVideoUpload: endPoint({
    method: 'POST',
    path: '/api/org-video-upload-complete',
    input: { payload: ApiCompleteIssueVideoUploadSchema },
    output: ApiCompleteIssueVideoUploadResponseSchema,
    openapi: { description: 'Complete an authenticated direct-to-S3 issue video upload.' },
  }),
  getIssueVideoSubmission: endPoint({
    method: 'GET',
    path: '/api/org-video',
    input: { key: string },
    output: ApiIssueVideoSubmissionSchema,
    openapi: { description: 'Get an issue video submission by ID.', keyName: 'issueVideoId' },
  }),
  listIssueVideoSubmissions: endPoint({
    method: 'GET',
    path: '/api/org-videos',
    input: { query: ApiIssueVideoSubmissionListQuerySchema },
    output: ApiIssueVideoSubmissionListResponseSchema,
    openapi: { description: 'List issue video submissions for a project.' },
  }),

  // ***********************************************************************************
  // Scan Video
  // ***********************************************************************************
  initScanVideoUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-upload-init',
    input: { payload: ApiInitScanVideoUploadSchema },
    output: ApiInitScanVideoUploadResponseSchema,
    openapi: { description: 'Initiate an authenticated direct-to-S3 scan walkthrough video upload.' },
  }),
  completeScanVideoUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-upload-complete',
    input: { payload: ApiCompleteScanVideoUploadSchema },
    output: ApiCompleteScanVideoUploadResponseSchema,
    openapi: { description: 'Complete a scan walkthrough video upload (persist metadata; analyze is started separately via POST /api/operation).' },
  }),
  getScanVideo: endPoint({
    method: 'GET',
    path: '/api/scan-video',
    input: { key: string },
    output: ApiScanVideoSubmissionSchema,
    openapi: { description: 'Get a scan walkthrough video by ID (includes playback URL when upload is complete).', keyName: 'scanVideoId' },
  }),
  getScanVideoData: endPoint({
    method: 'GET',
    path: '/api/scan-video-data',
    input: { key: string },
    output: z.object({}),
    openapi: { description: 'Stream scan video bytes by ID.', keyName: 'scanVideoId' },
  }),
  getScanVideosById: endPoint({
    method: 'POST',
    path: '/api/scan-videos-by-ids',
    input: { query: { validate: optionalQueryBoolean }, payload: ApiGetScanVideosByIdsPayloadSchema },
    output: [ApiScanVideoSchema],
    openapi: {
      description:
        'Get scan videos by IDs. Without ?validate, returns records directly from the DB. With ?validate, cross-references each record against S3 and only returns videos where the stored size matches.',
    },
  }),
  initScanVideoMultipartUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-multipart-init',
    input: { payload: ApiInitScanVideoMultipartUploadSchema },
    output: ApiInitScanVideoMultipartUploadResponseSchema,
    openapi: { description: 'Initiate (or resume) an S3 multipart upload for a large scan walkthrough video.' },
  }),
  signScanVideoUploadParts: endPoint({
    method: 'POST',
    path: '/api/scan-video-multipart-sign-parts',
    input: { payload: ApiSignScanVideoUploadPartsSchema },
    output: ApiSignScanVideoUploadPartsResponseSchema,
    openapi: { description: 'Presign a batch of part-upload URLs for an in-progress scan video multipart upload.' },
  }),
  completeScanVideoMultipartUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-multipart-complete',
    input: { payload: ApiCompleteScanVideoMultipartUploadSchema },
    output: ApiCompleteScanVideoUploadResponseSchema,
    openapi: { description: 'Complete a scan walkthrough multipart upload and enqueue scan video processing.' },
  }),
  abortScanVideoMultipartUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-multipart-abort',
    input: { payload: ApiAbortScanVideoMultipartUploadSchema },
    output: ApiAbortScanVideoMultipartUploadResponseSchema,
    openapi: { description: 'Abort an in-progress scan walkthrough multipart upload and discard its parts.' },
  }),
  getScanVideoMultipartStatus: endPoint({
    method: 'GET',
    path: '/api/scan-video-multipart-status',
    input: { key: string },
    output: ApiScanVideoMultipartStatusResponseSchema,
    openapi: { description: 'Report which parts of a scan walkthrough multipart upload have landed (for resume).', keyName: 'videoId' },
  }),

  // ***********************************************************************************
  // Scan Video Audio (narration segments)
  // ***********************************************************************************
  initScanVideoAudioUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-audio-upload-init',
    input: { payload: ApiInitScanVideoAudioUploadSchema },
    output: ApiInitScanVideoAudioUploadResponseSchema,
    openapi: { description: 'Presign PUT URL(s) for scan walkthrough narration audio segment upload(s).' },
  }),
  completeScanVideoAudioUpload: endPoint({
    method: 'POST',
    path: '/api/scan-video-audio-upload-complete',
    input: { payload: ApiCompleteScanVideoAudioUploadSchema },
    output: ApiCompleteScanVideoAudioUploadResponseSchema,
    openapi: { description: 'Persist uploaded narration audio segment(s) after S3 upload(s).' },
  }),
  getScanVideoAudio: endPoint({
    method: 'GET',
    path: '/api/scan-video-audio',
    input: { key: string },
    output: ApiScanVideoAudioSchema,
    openapi: { description: 'Get a scan walkthrough narration audio segment by ID.', keyName: 'scanVideoAudioId' },
  }),
  getScanVideoAudiosById: endPoint({
    method: 'POST',
    path: '/api/scan-video-audios-by-ids',
    input: { payload: ApiGetScanVideoAudiosByIdsPayloadSchema },
    output: [ApiScanVideoAudioSchema],
    openapi: { description: 'Get scan walkthrough narration audio segments by IDs.' },
  }),
  getScanVideoAudioData: endPoint({
    method: 'GET',
    path: '/api/scan-video-audio-data',
    input: { key: string },
    output: z.object({}),
    openapi: { description: 'Stream narration audio segment bytes by ID.', keyName: 'scanVideoAudioId' },
  }),

  // ***********************************************************************************
  // Scan Photo
  // ***********************************************************************************
  createScanPhoto: endPoint({
    method: 'POST',
    path: '/api/scan-photo',
    input: { payload: ApiCreateScanPhotoSchema, file: z.any() },
    output: ApiScanPhotoSchema,
    openapi: { description: 'Create a new scan photo.' },
  }),
  canCreateScanPhoto: endPoint({
    method: 'GET',
    path: '/api/scan-photo-can-create',
    input: { key: string },
    output: { canCreate: boolean, scanId: string },
    openapi: { description: 'Check if the user can create a scan photo.', keyName: 'scanId' },
  }),
  createScanPhotoBase64: endPoint({
    method: 'POST',
    path: '/api/scan-photo-base64',
    input: { payload: ApiCreateScanPhotoSchema.extend({ imageBase64: string, mimeType: optional(string) }) },
    output: ApiScanPhotoSchema,
    openapi: { description: 'Create a new scan photo from base64 data.', deprecated: true },
  }),
  getScanPhoto: endPoint({
    method: 'GET',
    path: '/api/scan-photo',
    input: { key: string },
    output: ApiScanPhotoSchema,
    openapi: { description: 'Get the scan photo by ID.', keyName: 'scanPhotoId' },
  }),
  getScanPhotoData: endPoint({
    method: 'GET',
    path: '/api/scan-photo-data',
    input: {
      key: string,
      query: {
        maxDimension: optional(numberOrNumberFromString),
        quality: optional(numberOrNumberFromString).meta({
          deprecated: true,
          description: 'quality parameter is deprecated. quality is now auto-selected to be optimally balanced between file size and quality.',
        }),
      },
    },
    output: z.object({}),
    openapi: { description: 'Get the scan photo data by ID.', keyName: 'scanPhotoId' },
  }),
  getScanPhotosById: endPoint({
    method: 'POST', // must be POST if we have a payload
    path: '/api/scan-photos-by-ids',
    input: { query: { validate: optionalQueryBoolean }, payload: { scanPhotoIds: zodLess([string]).max(100) } },
    output: [ApiScanPhotoSchema],
    openapi: {
      description:
        'Get scan photos by IDs. Without ?validate, returns records directly from the DB (fast, unverified). With ?validate, cross-references each record against S3 and only returns photos where the DB record exists, the S3 object exists, and the stored size matches. Records failing any check are silently skipped.',
    },
  }),

  // ***********************************************************************************
  // Scan Note
  // ***********************************************************************************
  createScanNote: endPoint({
    method: 'POST',
    path: '/api/scan-note',
    input: { payload: ApiCreateScanNoteSchema },
    output: ApiScanNoteSchema,
  }),
  getScanNotesByScanId: endPoint({
    method: 'GET',
    path: '/api/scan-note-by-scan-id',
    input: { key: string },
    output: [ApiScanNoteSchema],
    openapi: { description: 'Get scan notes by scan ID.', keyName: 'scanId' },
  }),
  bulkCreateScanNotes: endPoint({
    method: 'POST',
    path: '/api/scan-note-bulk-create',
    input: { key: string, payload: { data: [ApiCreateScanNoteSchema] } },
    output: { recordsWritten: number },
    openapi: {
      description: 'Bulk create scan notes to support offline note creation with subsequent sync. All notes must be for the same scan.',
      keyName: 'scanId',
    },
  }),

  // ***********************************************************************************
  // Scan Data Suggestion
  // ***********************************************************************************
  createScanDataSuggestion: endPoint({
    method: 'POST',
    path: '/api/scan-data-suggestion',
    input: { payload: ApiCreateScanDataSuggestionSchema },
    output: ApiScanDataSuggestionSchema,
    openapi: {
      description:
        'Create a pending suggested edit to one scan part. Idempotent when the client supplies the id. A new replace supersedes any pending replace for the same scan part.',
    },
  }),
  getScanDataSuggestion: endPoint({
    method: 'GET',
    path: '/api/scan-data-suggestion',
    input: { key: string },
    output: ApiScanDataSuggestionSchema,
    openapi: { description: 'Get a scan data suggestion by ID, whatever its status.', keyName: 'scanDataSuggestionId' },
  }),
  updateScanDataSuggestion: endPoint({
    method: 'PATCH',
    path: '/api/scan-data-suggestion',
    input: { key: string, payload: ApiUpdateScanDataSuggestionSchema },
    output: ApiScanDataSuggestionSchema,
    openapi: {
      description:
        'Accept, reject, or mark obsolete a pending scan data suggestion. Repeating the same decision returns the suggestion; changing a decision, or deciding a superseded suggestion, returns 400.',
      keyName: 'scanDataSuggestionId',
    },
  }),
  updateScanDataSuggestionsByScanVideoId: endPoint({
    method: 'PATCH',
    path: '/api/scan-data-suggestions-by-scan-video',
    input: { key: string, payload: ApiUpdateScanDataSuggestionsByScanVideoSchema },
    output: [ApiScanDataSuggestionSchema],
    openapi: {
      description:
        "Supersede a scan video's pending suggestions, before a re-run of the video writes new ones. Returns the suggestions it superseded; resolved ones keep their status.",
      keyName: 'scanVideoId',
    },
  }),
  getScanDataSuggestionsByScanId: endPoint({
    method: 'GET',
    path: '/api/scan-data-suggestions-by-scan',
    input: { key: string },
    output: [ApiScanDataSuggestionSchema],
    openapi: { description: 'Get the pending scan data suggestions for a scan, oldest first.', keyName: 'scanId' },
  }),

  // ***********************************************************************************
  // JEV (voice scope)
  // ***********************************************************************************
  evaluateJev: endPoint({
    method: 'POST',
    path: '/api/jev-evaluate',
    input: { payload: JevEvaluatePayloadSchema },
    output: JevEvaluateResponseSchema,
    openapi: { description: 'Proxy a JEV (TypeSafe System One) evaluation through the Kai server.' },
  }),

  // ***********************************************************************************
  // Scan Template
  // ***********************************************************************************
  getScanTemplate: endPoint({
    method: 'GET',
    path: '/api/scan-template',
    input: { key: string },
    output: ApiScanTemplateSchema,
    openapi: { description: 'Get the scan template by ID.', keyName: 'scanTemplateId' },
  }),
  getScanTemplates: endPoint({
    method: 'GET',
    path: '/api/scan-templates',
    input: { query: { archived: optionalQueryBoolean } },
    output: [ApiScanTemplateSchema],
    openapi: { description: 'Get scan templates by organization ID.', keyName: 'orgId' },
  }),
  createScanTemplate: endPoint({
    method: 'POST',
    path: '/api/scan-template',
    input: {
      payload: ApiCreateScanTemplateSchema.extend({
        scanTemplateRoot: optional(ScanTemplateRootSchema),
      }),
    },
    output: ApiScanTemplateSchema,
    openapi: { description: 'Create a new scan template.' },
  }),
  updateScanTemplate: endPoint({
    method: 'PATCH',
    path: '/api/scan-template',
    input: { key: string, payload: ApiUpdateScanTemplateSchema },
    output: ApiScanTemplateSchema,
    openapi: { description: 'Update an existing scan template.' },
  }),

  getScanTemplateRoot: endPoint({
    method: 'GET',
    path: '/api/scan-template-root',
    input: { key: string, query: { versionAt: optional(datetimeString) } },
    output: ScanTemplateRootSchema,
    openapi: { description: 'Get the full scan template root for a template, optionally at a specific version.', keyName: 'scanTemplateId' },
  }),
  getScanTemplatePartHistory: endPoint({
    method: 'GET',
    path: '/api/scan-template-part-history',
    input: { key: string, query: scanPartTreeHistoryQuery },
    output: scanPartTreeHistoryOutput,
    openapi: { description: 'Get the full version history of a scan template part and all its descendants (including overrides).', keyName: 'scanTemplateId' },
  }),
  getScanTemplateRootForScan: endPoint({
    method: 'GET',
    path: '/api/scan-template-root-for-scan',
    input: { key: string },
    output: ScanTemplateRootSchema,
    openapi: { description: 'Get the full scan template root for a scan.', keyName: 'scanId' },
  }),
  saveScanTemplateDataDelta: endPoint({
    method: 'POST',
    path: '/api/scan-template-data-delta',
    input: { key: string, payload: { data: [ScanDataInMemorySchema] } },
    output: { recordsWritten: number },
    openapi: { description: 'Save a delta of scan template data.', keyName: 'scanTemplateId' },
  }),

  // ***********************************************************************************
  // Scan
  // ***********************************************************************************
  getScan: endPoint({
    method: 'GET',
    path: '/api/scan',
    input: { key: string },
    output: ApiScanSchema,
    openapi: { description: 'Get scan by ID.', keyName: 'scanId' },
  }),
  getScansByScanTemplateId: endPoint({
    method: 'GET',
    path: '/api/scan-by-scan-template-id',
    input: { key: string },
    output: [ApiScanSchema],
    openapi: { description: 'Get scans by scan template ID.', keyName: 'scanTemplateId' },
  }),
  getScans: endPoint({
    method: 'GET',
    path: '/api/scans',
    input: { query: { scanLifecycleStatuses: optionalString, archived: optionalQueryBoolean } },
    output: [ApiScanSchema],
    openapi: { description: 'Get scans for the current organization with optional query params.' },
  }),
  getScansByPropertyProjectId: endPoint({
    method: 'GET',
    path: '/api/scan-by-property-project-id',
    input: { key: string, query: { archived: optionalQueryBoolean, includeAudienceDocs: optionalQueryBoolean } },
    output: [ApiScanSchema],
    openapi: { description: 'Get scans by property project ID.', keyName: 'propertyProjectId' },
  }),
  getScansForUser: endPoint({
    method: 'GET',
    path: '/api/scans-for-user',
    input: { key: string, query: { limit: optional(numberOrNumberFromString) } },
    output: [ApiScanSchema],
    openapi: { description: 'Get active scans for a user.', keyName: 'userId' },
  }),
  createScan: endPoint({
    method: 'POST',
    path: '/api/scan',
    input: { payload: ApiCreateScanSchema },
    output: ApiScanSchema,
    openapi: { description: 'Create a new scan.' },
  }),
  updateScan: endPoint({
    method: 'PATCH',
    path: '/api/scan',
    input: { key: string, payload: ApiUpdateScanSchema },
    output: ApiScanSchema,
    openapi: { description: 'Update an existing scan.' },
  }),

  getScanData: endPoint({
    method: 'GET',
    path: '/api/scan-data',
    input: {
      key: string,
      query: {
        from: optional(datetimeString).meta({
          description: 'The date to get the scan data from. from is exclusive - all returned scan data dates will be > `from`.',
        }),
        to: optional(datetimeString).meta({ description: 'The date to get the scan data to. to is inclusive - all returned scan data dates will be <= `to`.' }),
        syncedAfter: optional(datetimeString).meta({
          description:
            'When provided, only returns scan data records that were synced to the server after this date (syncedAt > syncedAfter). Use this for delta fetches to correctly capture records that were created offline and synced late, which may have an updatedAt earlier than the last fetch time.',
        }),
        includeBaseline: optionalQueryBoolean.meta({
          description:
            "Used when 'from' is set. If true, will ALSO return all baseline Scan data records at the given point in time provided by 'from'. This is the same data you would get if you only use the 'to' param and set it to the same value as 'from'.",
        }),
      },
    },
    output: {
      from: datetimeString.meta({ description: "The same value as the 'from' param input, if provided. Useful for managing deltas client-side." }),
      to: datetimeString.meta({ description: "The same value as the 'to' param input, if provided. Useful for managing deltas client-side." }),
      syncedAfter: optional(datetimeString).meta({
        description: "The same value as the 'syncedAfter' param input, if provided. Useful for managing deltas client-side.",
      }),
      data: zodLess([ScanDataInMemorySchema])
        .meta({
          description: `The scan data records for the given time range. If neither 'from' nor 'to' are provided,
will return the latest version of each ScanPartId. If 'to' is provided, then the 'latest
version' is <= 'to'. If 'from' is provided, then no records <= 'from'.`,
        }),
      baselineData: optional([ScanDataInMemorySchema])
        .meta({
          description:
            "If `from` and `includeBaseline` are set, will return all baseline scan data records from the creation of the scan up to and possibly equal to the given point in time provided by 'from'. This is the same data you would get if you only use the 'to' param and set it to the same value as 'from'.",
        }),
    },
    openapi: { description: 'Get the last update for each ScanPart as a ScanDataInMemory record for the given time range.', keyName: 'scanId' },
  }),
  getScanPartHistory: endPoint({
    method: 'GET',
    path: '/api/scan-part-history',
    input: { key: string, query: scanPartTreeHistoryQuery },
    output: scanPartTreeHistoryOutput,
    openapi: {
      description:
        "Get the full version history of a scan part and all its descendants across the scan's direct ancestry (ancestor scans up to each fork point; never peer branches).",
      keyName: 'scanId',
    },
  }),
  getScanRoot: endPoint({
    method: 'GET',
    path: '/api/scan-root',
    input: { key: string, query: { versionAt: optional(datetimeString) } },
    output: ScanRootSchema,
    openapi: { description: 'Get all the data for a scan at a point in time as a ScanRoot.', keyName: 'scanId' },
  }),
  saveScanDataDelta: endPoint({
    method: 'POST',
    path: '/api/scan-data-delta',
    input: { key: string, payload: { data: [ScanDataInMemorySchema] } },
    output: { recordsWritten: number },
    openapi: { description: 'Save a delta of scan data.', keyName: 'scanId' },
  }),
  cancelChangeOrder: endPoint({
    method: 'POST',
    path: '/api/scan-cancel-change-order',
    input: { key: string },
    output: ApiScanSchema,
    openapi: { description: "Cancel a change order by reverting scope changes and updating scan type from 'changeRequest' to 'progress'.", keyName: 'scanId' },
  }),
  refreshScanStats: endPoint({
    method: 'POST',
    path: '/api/scan-refresh-aggregations',
    input: { key: string },
    output: ApiScanSchema,
    openapi: {
      description:
        "Recompute the scan's derived aggregate fields (customerMetadata.photoCount, syncedPhotoCount, notesCount, cost totals, scanType) from current scan data and persist them. Useful for backfilling new aggregates without other edits to the scan.",
      keyName: 'scanId',
    },
  }),
  getScanPhotoStats: endPoint({
    method: 'GET',
    path: '/api/scan-photo-status',
    input: { key: string },
    output: ApiScanPhotoStatusSchema,
    openapi: { description: 'Get fresh photo status for a scan.', keyName: 'scanId' },
  }),

  // ***********************************************************************************
  // Operations (generic async-operation API — see ASYNC_OPERATION_API_RFC.md)
  //
  // Singular record / plural collection per the URI convention. One record shape
  // (ApiOperationSchema, discriminated on top-level operationType) is returned by all three.
  // ***********************************************************************************
  createOperation: endPoint({
    method: 'POST',
    path: '/api/operation',
    input: { payload: ApiCreateOperationSchema },
    output: ApiOperationSchema,
    openapi: {
      description:
        'Kick off a client-permitted operation by operationType with registry-validated params nested under input. Org/user are derived from the session; the operationType must be on the client-startable allowlist. Returns the created record (status "created"); poll it via GET /api/operation/:id.',
    },
  }),
  getOperation: endPoint({
    method: 'GET',
    path: '/api/operation',
    input: { key: string },
    output: ApiOperationSchema,
    openapi: {
      description:
        'Read + poll one operation: the full record with status and, narrowed on operationType, typed input/progressDetails/output. The single poll target — status signals completion (completed/failed), progress (0..1) and progressDetails signal where it is.',
      keyName: 'id',
    },
  }),
  listOperations: endPoint({
    method: 'GET',
    path: '/api/operations',
    input: { query: OperationListQuerySchema },
    output: [ApiOperationSchema],
    openapi: {
      description:
        'List operations for the current org — full records, identical in shape to the single read. Filter by CSV status (created,started,completed,failed; "still running" = created,started) and a single operationType. limit defaults to and is capped at 100, ordered updatedAt desc.',
    },
  }),

  // ***********************************************************************************
  // Scope Report
  // ***********************************************************************************
  exportScopePurchaseOrderExcel: endPoint({
    method: 'GET',
    path: '/api/scope-purchase-order-excel',
    input: { key: string, query: { reportType: ApiReportTypeSchema.default('fullScope') } },
    output: ApiBackgroundJobSchema,
    openapi: { description: "Export scan data as Excel. reportType can be 'fullScope' or 'changeOrder'.", keyName: 'scanId' },
  }),
  exportScopeReportPdf: endPoint({
    method: 'GET',
    path: '/api/scope-report-pdf',
    input: { key: string, query: { reportType: ApiReportTypeSchema.default('fullScope') } },
    output: ApiBackgroundJobSchema,
    openapi: { description: "Export scan data as PDF. reportType can be 'fullScope', 'changeOrder', 'punchlist', or 'closeout'.", keyName: 'scanId' },
  }),
  getScopeReportHtml: endPoint({
    method: 'GET',
    path: '/api/scope-report-html',
    input: { key: string, query: { userId: optional(string), reportType: ApiReportTypeSchema.default('fullScope') } },
    output: string,
    openapi: {
      description: "Get scan export as HTML for PDF generation. reportType can be 'fullScope', 'changeOrder', 'punchlist', or 'closeout'.",
      keyName: 'scanId',
    },
  }),
  generateReadOnlyToken: endPoint({
    method: 'GET',
    path: '/api/generate-read-only-token',
    input: {},
    output: zodLess({ token: string }),
    openapi: {
      description: 'Generate a temporary read-only token for browser-native downloads. Token expires in 30 minutes.',
      tags: ['auth'],
      product: 'Kai',
    },
  }),
  scopeReportPhotosZip: endPoint({
    method: 'GET',
    path: '/api/scope-report-photos-zip',
    input: { key: string, query: { token: string } },
    output: buffer,
    openapi: {
      description: 'Download all scope report photos as a streaming zip file. Requires a read-only token as query param.',
      keyName: 'scanId',
      tags: ['scope-report'],
      product: 'Kai',
    },
  }),
  exportBillingExcel: endPoint({
    method: 'GET',
    path: '/api/billing-excel-export',
    input: { query: { fromDatetime: datetimeString, toDatetime: datetimeString } },
    output: zodLess({ status: number, success: z.boolean() }),
    openapi: {
      description:
        'Enqueue a billing Excel export job for the given date range (all live orgs). Returns 200 and success; download link is emailed when the job completes.',
      tags: ['scope-report'],
      product: 'Kai',
    },
  }),
  calcTotalCostsByProductSupplier: endPoint({
    method: 'POST',
    path: '/api/calc-total-costs-by-product-supplier',
    input: { payload: { scanIds: optional(zodLess([string]).max(500)), fromDatetime: optional(datetimeString) } },
    output: zodLess({
      success: z.boolean(),
      jobs: [zodLess({ scanId: string, jobId: string })],
    }),
    openapi: {
      description:
        'Enqueue CalcTotalCostsByProductSupplier jobs for scanIds, or for all approved non-archived scans created at/after fromDatetime. Returns a list of enqueued job IDs.',
      tags: ['scope-report'],
      product: 'Kai',
    },
  }),

  // ***********************************************************************************
  // Retailer Product
  // ***********************************************************************************
  searchRetailerProducts: endPoint({
    method: 'GET',
    path: '/api/retailer-product-search',
    input: { query: RetailerProductSearchRequestQuerySchema },
    output: [RetailerProductSchema],
    openapi: { description: 'Search retailer products.' },
  }),
  searchCombinedProducts: endPoint({
    method: 'GET',
    path: '/api/combined-product-search',
    input: { query: CombinedProductSearchRequestQuerySchema },
    output: [CombinedProductSearchResponseSchema],
    openapi: { description: 'Search both retailer products and organization products combined.' },
  }),

  // ***********************************************************************************
  // OrgMarket
  // ***********************************************************************************
  getOrgMarkets: endPoint({
    method: 'GET',
    path: '/api/org-markets',
    input: { query: ArchivedListSearchQuerySchema },
    output: [ApiOrgMarketSchema],
    openapi: { description: 'Get markets for the current organization.' },
  }),
  getMarket: endPoint({
    method: 'GET',
    path: '/api/org-market',
    input: { key: string },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Get market.' },
  }),
  getMarketsByOrgContractorId: endPoint({
    method: 'GET',
    path: '/api/org-market-by-org-contractor-id',
    input: { key: string },
    output: [ApiOrgMarketSchema],
    openapi: { description: 'Get org markets by org contractor ID.', keyName: 'orgContractorId' },
  }),
  getMarketsByZipCode: endPoint({
    method: 'GET',
    path: '/api/org-market-by-zip-code',
    input: { query: { zipCode: string } },
    output: [ApiOrgMarketSchema],
    openapi: { description: 'Get org markets by zip code.' },
  }),
  createMarket: endPoint({
    method: 'POST',
    path: '/api/org-market',
    input: { payload: ApiCreateOrgMarketSchema },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Create a new market.' },
  }),
  bulkCreateMarkets: endPoint({
    method: 'POST',
    path: '/api/org-market-bulk-create',
    input: { payload: { data: [ApiCreateOrgMarketSchema] } },
    output: { count: number },
    openapi: { description: 'Bulk create markets.' },
  }),
  updateMarket: endPoint({
    method: 'PATCH',
    path: '/api/org-market',
    input: { key: string, payload: ApiUpdateOrgMarketSchema },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Update market.' },
  }),
  getMarketsByUserId: endPoint({
    method: 'GET',
    path: '/api/org-markets-by-user-id',
    input: { key: string },
    output: [ApiOrgMarketSchema],
    openapi: { description: 'Get users assigned to an org market.', keyName: 'orgMarketId' },
  }),
  getUsersByOrgMarketId: endPoint({
    method: 'GET',
    path: '/api/org-market-users',
    input: { key: string },
    output: [ApiUserSchema],
    openapi: { description: 'Get users assigned to an org market.', keyName: 'orgMarketId' },
  }),
  addUserToOrgMarket: endPoint({
    method: 'POST',
    path: '/api/org-market-add-user',
    input: { key: string, query: { userId: string } },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Add a user to a market.', keyName: 'orgMarketId' },
  }),
  removeUserFromOrgMarket: endPoint({
    method: 'POST',
    path: '/api/org-market-remove-user',
    input: { key: string, query: { userId: string } },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Remove a user from a market.', keyName: 'orgMarketId' },
  }),
  addContractorToMarket: endPoint({
    method: 'POST',
    path: '/api/org-market-add-contractor',
    input: { key: string, query: { orgContractorId: string } },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Add a contractor to a market.' },
  }),
  removeContractorFromMarket: endPoint({
    method: 'POST',
    path: '/api/org-market-remove-contractor',
    input: { key: string, query: { orgContractorId: string } },
    output: ApiOrgMarketSchema,
    openapi: { description: 'Remove a contractor from a market.' },
  }),

  // ***********************************************************************************
  // OrgContractor
  // ***********************************************************************************
  getOrgContractors: endPoint({
    method: 'GET',
    path: '/api/org-contractors',
    input: { query: ArchivedListSearchQuerySchema },
    output: [ApiOrgContractorSchema],
    openapi: { description: 'Get contractors for the current organization.' },
  }),
  getContractor: endPoint({
    method: 'GET',
    path: '/api/org-contractor',
    input: { key: string },
    output: ApiOrgContractorSchema,
    openapi: { description: 'Get contractor.' },
  }),
  getContractorsByOrgMarketId: endPoint({
    method: 'GET',
    path: '/api/org-contractor-by-org-market-id',
    input: { key: string },
    output: [ApiOrgContractorSchema],
    openapi: { description: 'Get org contractors by org market ID.', keyName: 'orgMarketId' },
  }),
  createContractor: endPoint({
    method: 'POST',
    path: '/api/org-contractor',
    input: { payload: ApiCreateOrgContractorSchema },
    output: ApiOrgContractorSchema,
    openapi: { description: 'Create a new contractor.' },
  }),
  bulkCreateContractors: endPoint({
    method: 'POST',
    path: '/api/org-contractor-bulk-create',
    input: { payload: { data: [ApiCreateOrgContractorSchema] } },
    output: { count: number },
    openapi: { description: 'Bulk create contractors.' },
  }),
  updateContractor: endPoint({
    method: 'PATCH',
    path: '/api/org-contractor',
    input: { key: string, payload: ApiUpdateOrgContractorSchema },
    output: ApiOrgContractorSchema,
    openapi: { description: 'Update contractor.' },
  }),
  addMarketToContractor: endPoint({
    method: 'POST',
    path: '/api/org-contractor-add-market',
    input: { key: string, query: { orgMarketId: string } },
    output: ApiOrgContractorSchema,
    openapi: { description: 'Add a market to a contractor.' },
  }),
  removeMarketFromContractor: endPoint({
    method: 'POST',
    path: '/api/org-contractor-remove-market',
    input: { key: string, query: { orgMarketId: string } },
    output: ApiOrgContractorSchema,
    openapi: { description: 'Remove a market from a contractor.' },
  }),
  bulkCreateContractorMarketRelationships: endPoint({
    method: 'POST',
    path: '/api/org-contractor-market-bulk-create',
    input: { payload: { data: [{ orgContractorId: string, orgMarketId: string }] } },
    output: { count: number },
    openapi: { description: 'Bulk create contractor-market relationships.' },
  }),

  // ***********************************************************************************
  // Contractor Link (spike — see kai-wiki/modules/contractor-onboarding.md)
  // ***********************************************************************************
  // Protected: mint a copyable contractor access link for one contractor on a scan.
  createContractorScanShareLink: endPoint({
    method: 'POST',
    path: '/api/scan-contractor-share-link',
    input: { key: string, query: { orgContractorId: string } },
    output: { url: string },
    openapi: { description: 'Create a contractor access link for one contractor on a scan, returning the shareable URL.', keyName: 'scanId' },
  }),
  // Public: validate an access-link token and return the welcome-screen context (redacted project).
  // The token is a PATH key (not ?token=) to avoid colliding with the auth-session middleware,
  // which reads ?token= as a bearer token.
  getContractorLinkInfo: endPoint({
    method: 'GET',
    path: '/api/contractor-link-info',
    input: { key: string },
    output: {
      project: optional(ApiPropertyProjectSchema),
      contractorName: string,
      orgName: optionalString,
      alreadyOnboarded: boolean,
      contactEmail: optionalString,
      contactPhoneNumber: optionalString,
    },
    openapi: {
      description: 'Validate a contractor access-link token and return the welcome-screen context (redacted project, owner org, on-file contacts).',
      keyName: 'token',
      tags: ['contractor-link'],
      product: 'Kai',
    },
  }),
  // Public: start a claim — send a short-lived claim (magic) link to the contractor's on-file contact.
  initiateContractorClaim: endPoint({
    method: 'POST',
    path: '/api/contractor-link-claim-initiate',
    input: { payload: { token: string, channel: optional('email|sms') } },
    output: { channel: 'email|sms', contact: string },
    openapi: { description: "Send a short-lived claim link to the contractor's on-file email or phone.", tags: ['contractor-link'], product: 'Kai' },
  }),
  // Public: consume a claim link — create/claim the org and return an authorized session token.
  claimContractorOrg: endPoint({
    method: 'POST',
    path: '/api/contractor-link-claim',
    input: { payload: { token: string } },
    output: { token: string, orgId: string, userId: string, scanId: optionalString, alreadyOnboarded: boolean },
    openapi: {
      description: 'Consume a contractor claim link: create the contractor org (or log in if it exists) and return an authorized session token.',
      tags: ['contractor-link'],
      product: 'Kai',
    },
  }),
  // Protected: the authenticated contractor lists the projects they're assigned to.
  getContractorProjects: endPoint({
    method: 'GET',
    path: '/api/contractor-projects',
    input: {},
    output: [ApiPropertyProjectSchema],
    openapi: { description: 'List the projects the authenticated contractor is assigned to (via the contractor-to-project link).' },
  }),

  // ***********************************************************************************
  // Audience Doc Share
  // ***********************************************************************************
  createAudienceDocShareLink: endPoint({
    method: 'POST',
    path: '/api/audience-doc-share-link',
    input: { key: string },
    output: ApiAudienceDocShareLinkCreateResponseSchema,
    openapi: {
      description: 'Create a secure public share link for one forked audience-doc scan.',
      keyName: 'scanId',
      tags: ['audience-doc-share'],
      product: 'Kai',
    },
  }),
  getAudienceDocShareLinkStatus: endPoint({
    method: 'GET',
    path: '/api/audience-doc-share-link-status',
    input: { key: string },
    output: ApiAudienceDocShareLinkStatusSchema,
    openapi: {
      description: 'Get the current secure-share status for one forked audience-doc scan.',
      keyName: 'scanId',
      tags: ['audience-doc-share'],
      product: 'Kai',
    },
  }),
  revokeAudienceDocShareLink: endPoint({
    method: 'DELETE',
    path: '/api/audience-doc-share-link',
    input: { key: string },
    output: ApiAudienceDocShareLinkRevokeResponseSchema,
    openapi: {
      description: 'Archive every secure public share link for one forked audience-doc scan.',
      keyName: 'scanId',
      tags: ['audience-doc-share'],
      product: 'Kai',
    },
  }),

  // ***********************************************************************************
  // OrgProduct
  // ***********************************************************************************
  getOrgProducts: endPoint({
    method: 'GET',
    path: '/api/org-products',
    input: { query: { archived: optionalQueryBoolean, limit: optional(numberOrNumberFromString), queryString: optionalString } },
    output: [ApiOrgProductSchema],
    openapi: { description: 'Get org products for the current organization.' },
  }),
  getOrgProduct: endPoint({
    method: 'GET',
    path: '/api/org-product',
    input: { key: string },
    output: ApiOrgProductSchema,
    openapi: { description: 'Get org product.' },
  }),
  getOrgProductBatch: endPoint({
    method: 'GET',
    path: '/api/org-product-batch',
    input: { query: { orgProductIds: string } },
    output: [ApiOrgProductSchema],
    openapi: { description: 'Get org product.' },
  }),
  vivifyOrgProduct: endPoint({
    method: 'POST',
    path: '/api/org-product',
    input: { payload: ApiCreateOrgProductSchema },
    output: ApiOrgProductSchema,
    openapi: { description: 'Create a new org product.' },
  }),
  updateOrgProduct: endPoint({
    method: 'PATCH',
    path: '/api/org-product',
    input: { key: string, payload: ApiUpdateOrgProductSchema },
    output: ApiOrgProductSchema,
    openapi: { description: 'Update org product.' },
  }),

  // ***********************************************************************************
  // OrgSettings (old)
  // ***********************************************************************************
  getOrgSetting: endPoint({
    method: 'GET',
    path: '/api/org-setting',
    input: { key: string },
    output: ApiOrgSettingsSchema,
    openapi: { description: 'Get organization setting by ID.', keyName: 'orgSettingId', deprecated: true },
  }),
  getOrgSettings: endPoint({
    method: 'GET',
    path: '/api/org-settings',
    input: {},
    output: z.array(ApiOrgSettingsSchema),
    openapi: { description: "Get all organization settings for the current user's org.", deprecated: true },
  }),
  updateOrgSettingsByKey: endPoint({
    method: 'PATCH',
    path: '/api/org-settings',
    input: { key: string, payload: ApiUpdateOrgSettingsSchema },
    output: ApiOrgSettingsSchema,
    openapi: { description: "Update organization settings by key for the current user's org.", keyName: 'settingsKey', deprecated: true },
  }),

  // ***********************************************************************************
  // AllOrgSettings
  // ***********************************************************************************
  getAllOrgSettings: endPoint({
    method: 'GET',
    path: '/api/all-org-settings',
    input: {},
    output: ApiAllOrgSettingsSchema,
    openapi: { description: "Get all organization settings for the current user's org." },
  }),
  updateAllOrgSettings: endPoint({
    method: 'PATCH',
    path: '/api/all-org-settings',
    input: { payload: ApiAllOrgSettingsSchema },
    output: ApiAllOrgSettingsSchema,
    openapi: { description: "Update one or more organization settings for the current user's org." },
  }),

  // ***********************************************************************************
  // UserToScan
  // ***********************************************************************************
  getUsersForScan: endPoint({
    method: 'GET',
    path: '/api/users-for-scan',
    input: { key: string },
    output: [ApiUserToScanWithUserSchema], /// TODO - should return USERS - based on the uniform data-structure principle - all user-returning endpoints should return users in the same structure
    openapi: { description: 'Get users assigned to a scan.', keyName: 'scanId' },
  }),
  assignUserToScan: endPoint({
    method: 'POST',
    path: '/api/user-to-scan',
    input: { payload: { userId: string, scanId: string } },
    output: ApiUserToScanSchema,
    openapi: { description: 'Assign a user to a scan.' },
  }),
  removeUserFromScan: endPoint({
    method: 'DELETE',
    path: '/api/user-to-scan',
    input: { payload: { userId: string, scanId: string } },
    output: ApiUserToScanSchema,
    openapi: { description: 'Remove a user from a scan.' },
  }),
};

// validate all endpoints have a unique method + path pair
const endpointMap = new Map<string, string>();
for (const [endpointName, endpoint] of Object.entries(KaiProApiEndpoints)) {
  const key = `${endpoint.method} ${endpoint.path}`;
  if (endpointMap.has(key)) {
    throw new Error(`Duplicate endpoint: ${endpointName} & ${endpointMap.get(key)}`);
  }
  endpointMap.set(key, endpointName);
}
